import React, { FC, useCallback, useEffect, useState } from "react";
import { Button, ButtonProps, Checkbox, Dialog, Input, LoadingSpinner, Select, Switch } from "@xapp/components";

import { AddLeadConnectIntegrationMutationVariables, LeadConnectNumber } from "generated/graphql";

import {
    CALLER_ID_NAME_MAX,
    DISPATCH_NUMBERS_MAX,
    LEAD_CONNECT_CONTACT_TYPES,
    LEAD_CONNECT_URGENCIES,
    LEAD_CONNECT_WEEKDAYS,
    LEAD_CONNECT_WEEKDAY_LABELS,
    LeadConnectCardState,
    LeadConnectFormValues,
    LeadConnectWeekday,
    formatCallerIdName,
    hasFormErrors,
    timeZoneOptions,
    toAddLeadConnectVariables,
    validateLeadConnectForm,
} from "./leadConnectForm";
import { LEAD_CONNECT_NAME } from "../integrationCatalog";

export type LeadConnectNumberStatus = Partial<Omit<LeadConnectNumber, "__typename">> & { state: string };

export interface LeadConnectDialogProps {
    active: boolean;
    state: LeadConnectCardState;
    /** The status query has not answered yet. */
    loading?: boolean;
    /** Starting values for the setup form, from the location. */
    defaults: LeadConnectFormValues;
    /** The business's Lead Connect number, once set up. */
    number?: LeadConnectNumberStatus | null;
    purchasing?: boolean;
    saving?: boolean;
    testing?: boolean;
    cancelling?: boolean;
    onClose(): void;
    /** Buy the add-on. */
    onPurchase?(): void;
    /** Save the setup with addLeadConnectIntegration. */
    onSetup?(variables: Omit<AddLeadConnectIntegrationMutationVariables, "appId">): void;
    /** "Call me with a sample lead". */
    onTestCall?(): void;
    /** Remove the add-on. */
    onCancelAddOn?(): void;
}

const CANCEL_TITLE = `Cancel ${LEAD_CONNECT_NAME}?`;

/** +12512869820 as (251) 286-9820. */
function formatNumber(e164: string | null | undefined): string {
    const match = (e164 || "").match(/^\+1(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : e164 || "";
}

function formatDate(iso: string | null | undefined): string {
    return iso ? new Date(iso).toLocaleDateString() : "";
}

/**
 * Lead Connect, a paid add-on: when a qualifying lead arrives it calls the business from its own
 * local number and, on a key press, bridges them to the homeowner.
 *
 * One dialog with three faces, chosen by the container from the add-on and the integration:
 * not bought (explain it and buy it), bought but not set up (the setup questions), set up (the
 * number's status, a test call, and cancelling). Presentational: the container owns the queries.
 */
export const LeadConnectDialog: FC<LeadConnectDialogProps> = (props) => {
    const { active, state, loading, defaults, number, purchasing, saving, testing, cancelling, onClose } = props;
    const { onPurchase, onSetup, onTestCall, onCancelAddOn } = props;

    const [values, setValues] = useState<LeadConnectFormValues>(defaults);
    // From the status face, the business can reopen the setup form to change it.
    const [editing, setEditing] = useState<boolean>(false);
    const [confirmCancel, setConfirmCancel] = useState<boolean>(false);

    // The defaults arrive with the status query; start the form from them once they do.
    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const errors = validateLeadConnectForm(values);
    const showForm = state === "setup" || (state === "status" && editing);

    const update = useCallback((change: Partial<LeadConnectFormValues>) => {
        setValues((current) => ({ ...current, ...change }));
    }, []);

    const updateNumber = useCallback(
        (index: number, change: Partial<LeadConnectFormValues["dispatchNumbers"][number]>) => {
            setValues((current) => ({
                ...current,
                dispatchNumbers: current.dispatchNumbers.map((dispatch, i) =>
                    i === index ? { ...dispatch, ...change } : dispatch
                ),
            }));
        },
        []
    );

    const addNumber = useCallback(() => {
        setValues((current) => ({
            ...current,
            dispatchNumbers: [...current.dispatchNumbers, { label: "", number: "" }],
        }));
    }, []);

    const removeNumber = useCallback((index: number) => {
        setValues((current) => ({
            ...current,
            dispatchNumbers: current.dispatchNumbers.filter((_, i) => i !== index),
        }));
    }, []);

    const toggleIn = useCallback((key: "urgencies" | "contactTypes", value: string, checked: boolean) => {
        setValues((current) => ({
            ...current,
            [key]: checked
                ? [...current[key].filter((v) => v !== value), value]
                : current[key].filter((v) => v !== value),
        }));
    }, []);

    const setDay = useCallback((day: LeadConnectWeekday, hours: { open: string; close: string } | undefined) => {
        setValues((current) => {
            const weekly = { ...current.weekly };
            if (hours) {
                weekly[day] = hours;
            } else {
                delete weekly[day];
            }
            return { ...current, weekly };
        });
    }, []);

    const handleSave = useCallback(() => {
        if (typeof onSetup === "function") {
            onSetup(toAddLeadConnectVariables(values));
            setEditing(false);
        }
    }, [onSetup, values]);

    const handleClose = useCallback(() => {
        setEditing(false);
        setValues(defaults);
        onClose();
    }, [defaults, onClose]);

    const handleConfirmCancel = useCallback(() => {
        setConfirmCancel(false);
        if (typeof onCancelAddOn === "function") {
            onCancelAddOn();
        }
    }, [onCancelAddOn]);

    const actions: ButtonProps[] = [{ label: "Close", variation: "secondary", onClick: handleClose }];
    let title = LEAD_CONNECT_NAME;
    let body: JSX.Element;

    if (loading) {
        body = <LoadingSpinner loadingText={`Loading ${LEAD_CONNECT_NAME}...`} />;
    } else if (state === "unsubscribed") {
        title = `Add ${LEAD_CONNECT_NAME}`;
        if (typeof onPurchase === "function") {
            actions.push({
                label: `Add ${LEAD_CONNECT_NAME}`,
                variation: "primary",
                disabled: !!purchasing,
                onClick: onPurchase,
            });
        }
        body = (
            <div data-lead-connect-pitch>
                <p>
                    When a lead worth calling comes in, {LEAD_CONNECT_NAME} calls you from your own local number, tells you
                    about the lead, and connects you to the homeowner when you press 1.
                </p>
                <p>
                    It is a paid add-on to this app. Once it is added you choose the caller ID name, the numbers to call
                    and which leads are worth a call.
                </p>
            </div>
        );
    } else if (showForm) {
        title = state === "setup" ? `Set Up ${LEAD_CONNECT_NAME}` : `Change ${LEAD_CONNECT_NAME} Setup`;
        if (state === "status") {
            actions.push({ label: "Back", variation: "secondary", onClick: () => setEditing(false) });
        }
        if (typeof onSetup === "function") {
            actions.push({
                label: "Save",
                variation: "primary",
                disabled: hasFormErrors(errors) || !!saving,
                onClick: handleSave,
            });
        }
        body = renderForm();
    } else {
        if (typeof onSetup === "function") {
            actions.push({ label: "Change Setup", variation: "secondary", onClick: () => setEditing(true) });
        }
        body = renderStatus();
    }

    function renderForm(): JSX.Element {
        const callerIdName = values.callerIdName;
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* The API does not return the saved setup, so the form starts from the location. */}
                {state === "status" && (
                    <p data-replaces-setup style={{ margin: 0, color: "#d32f2f" }}>
                        This form starts from your location&apos;s details, not your current setup. Saving replaces your
                        numbers to call, calling hours and lead choices, so check every section first.
                    </p>
                )}
                <h4 style={{ margin: "8px 0 0" }}>Your {LEAD_CONNECT_NAME} number</h4>
                <Input
                    label="Area code"
                    value={values.areaCode}
                    error={!!errors.areaCode}
                    helperText={
                        errors.areaCode || "We look for a number in this area code. Leave it blank to let us pick."
                    }
                    onChange={(value) => update({ areaCode: String(value).replace(/\D/g, "").slice(0, 3) })}
                />
                <Input
                    label="Caller ID name"
                    value={callerIdName}
                    error={!!errors.callerIdName}
                    helperText={errors.callerIdName || `${callerIdName.length}/${CALLER_ID_NAME_MAX}`}
                    onChange={(value) => update({ callerIdName: formatCallerIdName(String(value)) })}
                />

                <h4 style={{ margin: "8px 0 0" }}>Numbers to call</h4>
                <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>Called in order until someone answers.</p>
                {values.dispatchNumbers.map((dispatch, index) => {
                    const numberError = errors.dispatchNumberErrors?.[index];
                    return (
                        <div
                            key={`dispatch-${index}`}
                            style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
                        >
                            <Input
                                label={`Label ${index + 1}`}
                                value={dispatch.label}
                                onChange={(value) => updateNumber(index, { label: String(value) })}
                            />
                            <Input
                                label={`Number ${index + 1}`}
                                type="tel"
                                value={dispatch.number}
                                error={!!numberError}
                                helperText={numberError}
                                onChange={(value) => updateNumber(index, { number: String(value) })}
                            />
                            {values.dispatchNumbers.length > 1 && (
                                <Button data-remove-number variation="secondary" onClick={() => removeNumber(index)}>
                                    Remove
                                </Button>
                            )}
                        </div>
                    );
                })}
                {errors.dispatchNumbers && <p style={{ margin: 0, color: "#d32f2f" }}>{errors.dispatchNumbers}</p>}
                {values.dispatchNumbers.length < DISPATCH_NUMBERS_MAX && (
                    <div>
                        <Button data-add-number variation="secondary" onClick={addNumber}>
                            Add a number
                        </Button>
                    </div>
                )}

                <h4 style={{ margin: "8px 0 0" }}>When to call</h4>
                <Switch
                    label="Use my business hours"
                    checked={values.useBusinessHours}
                    onChange={(checked) => update({ useBusinessHours: checked })}
                />
                {!values.useBusinessHours && (
                    <div data-custom-hours style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Select
                            label="Time zone"
                            fullWidth
                            items={timeZoneOptions(values.timeZone)}
                            value={values.timeZone}
                            onChange={(value) => update({ timeZone: String(value) })}
                        />
                        {LEAD_CONNECT_WEEKDAYS.map((day) => {
                            const hours = values.weekly[day];
                            return (
                                <div key={`hours-${day}`} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                    <Checkbox
                                        label={LEAD_CONNECT_WEEKDAY_LABELS[day]}
                                        checked={!!hours}
                                        onChange={(checked) =>
                                            setDay(day, checked ? { open: "08:00", close: "17:00" } : undefined)
                                        }
                                    />
                                    {hours && (
                                        <>
                                            <Input
                                                label={`${LEAD_CONNECT_WEEKDAY_LABELS[day]} opens`}
                                                type="time"
                                                value={hours.open}
                                                onChange={(value) => setDay(day, { ...hours, open: String(value) })}
                                            />
                                            <Input
                                                label={`${LEAD_CONNECT_WEEKDAY_LABELS[day]} closes`}
                                                type="time"
                                                value={hours.close}
                                                onChange={(value) => setDay(day, { ...hours, close: String(value) })}
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                        {errors.hours && <p style={{ margin: 0, color: "#d32f2f" }}>{errors.hours}</p>}
                    </div>
                )}

                <h4 style={{ margin: "8px 0 0" }}>Which leads</h4>
                <Switch
                    label="High-value leads only"
                    checked={values.highValueOnly}
                    onChange={(checked) => update({ highValueOnly: checked })}
                />
                <div>
                    <p style={{ margin: "0 0 4px", fontSize: "13px", color: "#666" }}>
                        Urgency (none picked calls about any)
                    </p>
                    {LEAD_CONNECT_URGENCIES.map((urgency) => (
                        <Checkbox
                            key={urgency.value}
                            data-urgency={urgency.value}
                            label={urgency.label}
                            checked={values.urgencies.includes(urgency.value)}
                            onChange={(checked) => toggleIn("urgencies", urgency.value, checked)}
                        />
                    ))}
                </div>
                <div>
                    <p style={{ margin: "0 0 4px", fontSize: "13px", color: "#666" }}>
                        Contact type (none picked calls about any)
                    </p>
                    {LEAD_CONNECT_CONTACT_TYPES.map((contactType) => (
                        <Checkbox
                            key={contactType.value}
                            data-contact-type={contactType.value}
                            label={contactType.label}
                            checked={values.contactTypes.includes(contactType.value)}
                            onChange={(checked) => toggleIn("contactTypes", contactType.value, checked)}
                        />
                    ))}
                </div>
            </div>
        );
    }

    function renderNumberState(): JSX.Element {
        switch (number?.state) {
            case "active":
                return <p style={{ margin: 0 }}>Active</p>;
            case "provisioning":
                return <p style={{ margin: 0 }}>Getting your number. This usually takes a minute or two.</p>;
            case "releasing":
                return (
                    <p style={{ margin: 0 }}>
                        Releasing on {formatDate(number.releaseAt)}. Calls have stopped. Save the setup again before
                        then to keep this number.
                    </p>
                );
            case "released":
                return <p style={{ margin: 0 }}>Released. Save the setup again to get a new number.</p>;
            case "failed":
                return (
                    <p style={{ margin: 0, color: "#d32f2f" }}>
                        {number.error || `${LEAD_CONNECT_NAME} could not get a number for this business.`}
                    </p>
                );
            default:
                return <p style={{ margin: 0 }}>No number yet.</p>;
        }
    }

    function renderStatus(): JSX.Element {
        const canTestCall = number?.state === "active" && !testing;
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div data-lead-connect-status>
                    <h4 style={{ margin: "0 0 4px" }}>Your {LEAD_CONNECT_NAME} number</h4>
                    {number?.number && (
                        <p style={{ margin: "0 0 4px", fontSize: "18px" }}>{formatNumber(number.number)}</p>
                    )}
                    {renderNumberState()}
                    {number?.cnamName && (
                        <p style={{ margin: "8px 0 0" }}>
                            Caller ID name: <strong>{number.cnamName}</strong>
                            {number.cnamStatus === "listed"
                                ? " (listed with carriers)"
                                : " (pending: carriers take 1-3 days to show it)"}
                        </p>
                    )}
                    {number?.note && <p style={{ margin: "8px 0 0", color: "#666" }}>{number.note}</p>}
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <Button data-test-call variation="primary" disabled={!canTestCall} onClick={onTestCall}>
                        Call me with a sample lead
                    </Button>
                    {typeof onCancelAddOn === "function" && (
                        <Button
                            data-cancel-add-on
                            variation="secondary"
                            disabled={!!cancelling}
                            onClick={() => setConfirmCancel(true)}
                        >
                            Cancel {LEAD_CONNECT_NAME}
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            <Dialog active={active} actions={actions} title={title} onClose={handleClose} maxWidth="md" fullWidth>
                {body}
            </Dialog>
            <Dialog
                active={confirmCancel}
                title={CANCEL_TITLE}
                onClose={() => setConfirmCancel(false)}
                actions={[
                    { label: `Keep ${LEAD_CONNECT_NAME}`, variation: "secondary", onClick: () => setConfirmCancel(false) },
                    { label: `Cancel ${LEAD_CONNECT_NAME}`, variation: "primary", onClick: handleConfirmCancel },
                ]}
            >
                <p>{LEAD_CONNECT_NAME} stops calling about new leads. Calls already in progress still finish.</p>
                <p>Your number is kept for 30 days. Add {LEAD_CONNECT_NAME} again before then to keep it.</p>
            </Dialog>
        </>
    );
};
